import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog, useConfirm } from "./helpers";

const DOCTOR_MENU = [
  {
    label: "Main",
    items: [
      { id: "doctor_dashboard", label: "Dashboard", icon: "🏠", badge: null },
      {
        id: "my_appointments",
        label: "Today's Appointments",
        icon: "📅",
        badge: null,
      },
      { id: "availability", label: "Manage Schedule", icon: "🗓️", badge: null },
      { id: "my_patients", label: "My Patients", icon: "👥", badge: null },
    ],
  },
  {
    label: "Account",
    items: [
      //{ id: "doctor_settings", label: "Settings", icon: "⚙️", badge: null },
      {
        id: "drprofile",
        label: "My Profile",
        icon: "👤",
        path: "/doctor/drprofile",
      },
    ],
  },
];

function ToothIcon({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C9 2 6.5 4.2 6.5 7C6.5 8.6 7.1 9.7 7.1 11.2C7.1 13.8 6 16.2 6 18.6C6 20.6 7 22 8.5 22C10 22 10.5 20 12 20C13.5 20 14 22 15.5 22C17 22 18 20.6 18 18.6C18 16.2 16.9 13.8 16.9 11.2C16.9 9.7 17.5 8.6 17.5 7C17.5 4.2 15 2 12 2Z"
        fill={color}
      />
      <path
        d="M9.5 7.5C9.5 6 10.6 5 12 5C13.4 5 14.5 6 14.5 7.5"
        stroke="white"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export default function DoctorSidebar({
  user = (() => {
    try {
      const stored = localStorage.getItem("adminuser");
      if (!stored) return { name: "Doctor", email: "" };
      const parsed = JSON.parse(stored);
      if (typeof parsed === "string")
        return { name: parsed || "Doctor", email: "" };
      if (typeof parsed === "object" && parsed !== null) {
        return {
          name: parsed.name || parsed.Name || "Doctor",
          email: parsed.email || parsed.Email || "",
        };
      }
      return { name: "Doctor", email: "" };
    } catch {
      return { name: "Doctor", email: "" };
    }
  })(),
  activeMenu = "",
  onMenuChange,
  onLogout,
}) {
  const navigate = useNavigate();
  const { confirm, confirmProps } = useConfirm();
  const [active, setActive] = useState(activeMenu);
  const [logoutHover, setLogoutHover] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const accent = "#378ADD";
  const avatarColor = "#85B7EB";
  const initials = (user.name || "D")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleClick = (item) => {
  setActive(item.id);

  if (item.path) {
    navigate(item.path);
  } else if (onMenuChange) {
    onMenuChange(item.id);
  }
};

  const handleLogout = async () => {
    const ok = await confirm({
      title: "Logout",
      message:
        "Are you sure you want to logout? You will be redirected to the login page.",
      confirmText: "Yes, Logout",
      cancelText: "Stay",
      variant: "danger",
      icon: "🚪",
    });
    if (!ok) return;

    setLogoutLoading(true);
    localStorage.removeItem("islogin");
    localStorage.removeItem("user");
    localStorage.removeItem("adminuser");
    localStorage.removeItem("logintype");
    localStorage.removeItem("rememberEmail");
    sessionStorage.clear();
    if (typeof onLogout === "function") await onLogout();
    navigate("/", { replace: true });
  };

  return (
    <>
      <ConfirmDialog {...confirmProps} />
      <div
        className="d-flex flex-column"
        style={{ height: "100vh", background: "#0A1628", overflow: "hidden" }}
      >
        {/* Logo */}
        <div
          className="px-3 pt-3 pb-3 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{
                width: 38,
                height: 38,
                background: `${accent}22`,
                border: `1px solid ${accent}55`,
              }}
            >
              <ToothIcon color={accent} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.3px",
                }}
              >
                OM
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                Dental Clinic
              </div>
            </div>
          </div>
        </div>

        {/* Role Indicator */}
        <div
          className="mx-3 mt-2 mb-1 px-3 py-2 rounded-3 flex-shrink-0"
          style={{ background: `${accent}15`, border: `1px solid ${accent}35` }}
        >
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: accent,
                display: "inline-block",
                boxShadow: `0 0 6px ${accent}`,
              }}
            />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: accent,
                letterSpacing: ".6px",
                textTransform: "uppercase",
              }}
            >
              Doctor Panel
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav
          className="flex-grow-1 px-2 pb-2"
          style={{ overflowY: "auto", overflowX: "hidden" }}
        >
          {DOCTOR_MENU.map((section) => (
            <div key={section.label}>
              <div
                className="px-2 mt-3 mb-1"
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "1.1px",
                  color: "rgba(255,255,255,0.22)",
                  textTransform: "uppercase",
                }}
              >
                {section.label}
              </div>
              {section.items.map((item) => {
                const isActive = active === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleClick(item)}
                    className="d-flex align-items-center gap-2 px-3 py-2 rounded-3 mb-1"
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      background: isActive ? `${accent}22` : "transparent",
                      color: isActive ? accent : "rgba(255,255,255,0.55)",
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 500,
                      borderLeft: isActive
                        ? `3px solid ${accent}`
                        : "3px solid transparent",
                      transition: "all .15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background =
                          "rgba(255,255,255,0.07)";
                        e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "rgba(255,255,255,0.55)";
                      }
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{
                        width: 28,
                        height: 28,
                        fontSize: 14,
                        background: isActive ? `${accent}30` : "transparent",
                      }}
                    >
                      {item.icon}
                    </div>
                    <span className="flex-grow-1 text-truncate">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Profile */}
        <div
          className="px-2 py-3 flex-shrink-0"
          style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div
            className="d-flex align-items-center gap-2 px-2 py-2 rounded-3 mb-2"
            style={{ background: "rgba(255,255,255,0.05)" }}
          >
            <div
              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                width: 32,
                height: 32,
                background: `${accent}28`,
                border: `1px solid ${accent}50`,
                fontSize: 11,
                fontWeight: 700,
                color: avatarColor,
              }}
            >
              {initials}
            </div>
            <div className="flex-grow-1 overflow-hidden">
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.name}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.35)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.email}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            onMouseEnter={() => setLogoutHover(true)}
            onMouseLeave={() => setLogoutHover(false)}
            disabled={logoutLoading}
            className="btn w-100 d-flex align-items-center justify-content-center gap-2 py-2"
            style={{
              background: logoutHover
                ? "rgba(226,75,74,0.15)"
                : "rgba(255,255,255,0.05)",
              color: logoutHover ? "#E24B4A" : "rgba(255,255,255,0.45)",
              border: `1px solid ${logoutHover ? "rgba(226,75,74,0.35)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 600,
              transition: "all .15s",
              opacity: logoutLoading ? 0.6 : 1,
              cursor: logoutLoading ? "not-allowed" : "pointer",
            }}
          >
            {logoutLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  style={{ width: 13, height: 13, borderWidth: 2 }}
                />{" "}
                Logging out...
              </>
            ) : (
              <>
                <LogoutIcon /> Logout
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
